/**
 * Välj fordonsplacering på karta (en punkt) – liknande utlämningsplats men utan polygon.
 */

import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { LatLng } from 'leaflet';
import { useTranslation } from '@/store/languageStore';
import { reverseGeocode } from '@/services/geocoding/geocodingApi';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER: [number, number] = [59.33, 18.07];

interface PlacementPointMapModalProps {
  initialLat: number | null;
  initialLng: number | null;
  onSave: (lat: number, lng: number, address?: { address?: string; postalCode?: string; city?: string }) => void;
  onClose: () => void;
}

function MapClickPicker({
  position,
  setPosition,
}: {
  position: LatLng | null;
  setPosition: (p: LatLng) => void;
}) {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });
  return position ? <Marker position={position} /> : null;
}

function CenterOnPoint({ position }: { position: LatLng | null }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView([position.lat, position.lng], 15);
    }
  }, [map, position]);
  return null;
}

export default function PlacementPointMapModal({
  initialLat,
  initialLng,
  onSave,
  onClose,
}: PlacementPointMapModalProps) {
  const { t } = useTranslation();
  const [position, setPosition] = useState<LatLng | null>(
    initialLat != null && initialLng != null ? new LatLng(initialLat, initialLng) : null
  );
  const [saving, setSaving] = useState(false);

  const center: [number, number] = position
    ? [position.lat, position.lng]
    : initialLat != null && initialLng != null
      ? [initialLat, initialLng]
      : DEFAULT_CENTER;

  const handleSave = async () => {
    if (!position) return;
    setSaving(true);
    try {
      const addr = await reverseGeocode(position.lat, position.lng);
      onSave(position.lat, position.lng, {
        address:
          addr?.street && addr?.houseNumber
            ? `${addr.street} ${addr.houseNumber}`
            : addr?.address,
        postalCode: addr?.postcode,
        city: addr?.city,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-bgElevated rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">{t('vehicleCard.placement.pickOnMap')}</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={!position || saving}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded">
              {t('common.cancel')}
            </button>
          </div>
        </div>
        <div className="relative w-full h-[400px]">
          <MapContainer center={center} zoom={13} className="z-0 h-full w-full">
            <TileLayer url="https://app.deque.se/api/map/tiles/{z}/{x}/{y}.png?v=2" attribution="© OpenMapTiles © OpenStreetMap" />
            <CenterOnPoint position={position} />
            <MapClickPicker position={position} setPosition={setPosition} />
          </MapContainer>
        </div>
        <p className="p-2 text-sm text-textSecondary border-t border-border">
          {t('vehicleCard.placement.mapHelp')}
        </p>
      </div>
    </div>
  );
}
